import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { environment } from '@env/environment';
import { CredentialsService } from '@app/core/authentication/credentials.service';
import { HttpService } from '@app/core/http/http.service';

/**
 * Event-Codes müssen mit dem Backend-Katalog (trackingbundle) übereinstimmen.
 * Nur FRONTEND/BOTH-Codes dürfen hier gesendet werden – bewertete Events
 * (QUESTION_END, EXAM_END, *_COMPLETED, CERTIFICATE_*) stempelt das Backend selbst.
 */
export type TrackingEventCode =
  | 'PAGE_ENTER'
  | 'PAGE_LEAVE'
  | 'VIDEO_START'
  | 'VIDEO_END'
  | 'QUESTION_START'
  | 'QUESTION_ABORT'
  | 'PRACTICE_START'
  | 'PRACTICE_END'
  | 'EXAM_START';

/**
 * Kurs + Session werden NICHT mehr vom Client gesendet: das Backend hält die
 * Tracking-Session serverseitig in Redis (gesetzt beim Kurs-Öffnen/Page-Load) und
 * leitet Kurs+Session bei der Ingestion ab. Der Client sendet nur Lektions-/Objekt-
 * Referenzen (Hashes; Backend dekodiert via GetIdFromHash) plus die url_tree
 * (für die Startseiten-Erkennung).
 */
interface TrackingInput {
  event_code: TrackingEventCode;
  lesson_ref: string;
  url_tree: string;
  object_type: string;
  object_ref: string;
  client_time: string;
  payload?: Record<string, unknown>;
}

interface CurrentPage {
  urlTree: string;
  lessonRef: string;
  enter: number;
}

/**
 * Erfasst Bewegungsdaten (Seitenaufrufe/Verweildauer, Video-Start/-Ende,
 * Quiz-/Fragen-Start) und sendet sie gebündelt an POST /track.
 *
 * Bewusst KEIN Event pro Video-timeupdate (feuert pro Frame) – nur Start/Ende;
 * Verweildauer über PAGE_ENTER/PAGE_LEAVE.
 */
@Injectable({ providedIn: 'root' })
export class TrackingService implements OnDestroy {
  private buffer: TrackingInput[] = [];
  private currentPage: CurrentPage | null = null;
  // Zuletzt bekannte Lektions-Referenz (Hash), aus kurs-scoped API-URLs der Komponenten
  // oder der Browser-URL. (Kurs kommt serverseitig aus der Session.)
  private currentLessonRef = '';
  private flushTimer: ReturnType<typeof setInterval> | null = null;
  private readonly flushIntervalMs = 12000;
  private readonly maxBuffer = 50;
  private readonly url =
    environment.serverUrl + environment.apiVersion + '/track';

  constructor(
    private router: Router,
    private ngZone: NgZone,
    private http: HttpService,
    private credentialsService: CredentialsService,
  ) {
    // Navigation global abonnieren (deckt auch /home ab).
    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe(() => this.onNavigation(this.router.url));

    this.ngZone.runOutsideAngular(() => {
      this.flushTimer = setInterval(() => this.flush(), this.flushIntervalMs);
      // Beim Verlassen/Verstecken der Seite letzte Verweildauer + Buffer sichern.
      window.addEventListener('pagehide', () => this.onUnload());
      window.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') this.onUnload();
      });
    });
  }

  ngOnDestroy(): void {
    if (this.flushTimer) clearInterval(this.flushTimer);
  }

  // ---- Kontext ----

  /**
   * Aktualisiert die aktuelle Lektions-Referenz aus einer kurs-scoped API-URL
   * (z.B. video_watch_url, data_url_questions, componentBaseUrl), die den Lektions-Hash
   * zuverlässig enthält. Von den Komponenten vor dem Emit aufgerufen.
   */
  noteContext(apiUrl: string): void {
    const lesson = this.extractSeg(apiUrl, 'lesson');
    if (lesson) this.currentLessonRef = lesson;
  }

  // ---- Öffentliche Hooks für Komponenten (Refs = Hashes) ----

  trackVideoStart(videoRef: string, apiUrl?: string): void {
    this.enqueue('VIDEO_START', 'VIDEO', videoRef, { apiUrl });
  }

  trackVideoEnd(
    videoRef: string,
    positionSeconds?: number,
    apiUrl?: string,
  ): void {
    this.enqueue('VIDEO_END', 'VIDEO', videoRef, {
      apiUrl,
      payload: { position_s: positionSeconds ?? null },
    });
  }

  trackQuestionStart(questionRef: string, apiUrl?: string): void {
    this.enqueue('QUESTION_START', 'QUESTION', questionRef, { apiUrl });
  }

  /** Nutzer hat die Frage über den Schließen-Button abgebrochen. */
  trackQuestionAbort(questionRef: string, apiUrl?: string): void {
    this.enqueue('QUESTION_ABORT', 'QUESTION', questionRef, {
      apiUrl,
      payload: { aborted: true },
    });
  }

  /** Übungssitzung (Übungsfragen) – Objekt ist die Lektion. */
  trackPracticeStart(lessonRef: string, apiUrl?: string): void {
    this.enqueue('PRACTICE_START', 'LESSON', lessonRef, { apiUrl, lessonRef });
  }

  trackPracticeEnd(lessonRef: string, apiUrl?: string): void {
    this.enqueue('PRACTICE_END', 'LESSON', lessonRef, { apiUrl, lessonRef });
  }

  /** Probeprüfung gestartet. Ende + Ergebnis stempelt das Backend als EXAM_END. */
  trackExamStart(testexamRef: string, apiUrl?: string): void {
    this.enqueue('EXAM_START', 'TESTEXAM', testexamRef, { apiUrl });
  }

  // ---- Intern ----

  private onNavigation(urlTree: string): void {
    if (this.currentPage) {
      this.emitPageLeave(this.currentPage);
    }
    if (this.isHome(urlTree)) {
      this.currentLessonRef = '';
    } else {
      const lesson = this.extractSeg(urlTree, 'lesson');
      this.currentLessonRef = lesson;
    }
    this.currentPage = {
      urlTree,
      lessonRef: this.currentLessonRef,
      enter: Date.now(),
    };
    this.push({
      event_code: 'PAGE_ENTER',
      lesson_ref: this.currentLessonRef,
      url_tree: urlTree,
      object_type: 'PAGE',
      object_ref: '',
      client_time: new Date().toISOString(),
      payload: { page: this.pageLabel(urlTree) },
    });
  }

  private emitPageLeave(page: CurrentPage): void {
    this.push({
      event_code: 'PAGE_LEAVE',
      lesson_ref: page.lessonRef,
      url_tree: page.urlTree,
      object_type: 'PAGE',
      object_ref: '',
      client_time: new Date().toISOString(),
      payload: {
        page: this.pageLabel(page.urlTree),
        duration_ms: Date.now() - page.enter,
      },
    });
  }

  private enqueue(
    code: TrackingEventCode,
    objectType: string,
    objectRef: string,
    opts?: {
      apiUrl?: string;
      lessonRef?: string;
      payload?: Record<string, unknown>;
    },
  ): void {
    if (opts?.apiUrl) {
      this.noteContext(opts.apiUrl);
    }
    this.push({
      event_code: code,
      lesson_ref: opts?.lessonRef ?? this.currentLessonRef,
      url_tree: this.router.url,
      object_type: objectType,
      object_ref: objectRef ?? '',
      client_time: new Date().toISOString(),
      payload: opts?.payload,
    });
  }

  private push(event: TrackingInput): void {
    this.buffer.push(event);
    if (this.buffer.length >= this.maxBuffer) {
      this.flush();
    }
  }

  /** Regulärer Flush über HttpService (mit Interceptor-Auth). */
  private flush(): void {
    if (this.buffer.length === 0 || !this.credentialsService.token) return;
    const batch = this.buffer;
    this.buffer = [];
    this.http.post('/track', batch).subscribe({
      error: () => {
        // Bei Fehler zurücklegen, damit nichts verloren geht.
        this.buffer = batch.concat(this.buffer);
      },
    });
  }

  /** Unload-Flush: fetch keepalive (sendBeacon kann keinen Auth-Header setzen). */
  private onUnload(): void {
    if (this.currentPage) {
      this.emitPageLeave(this.currentPage);
      this.currentPage = null;
    }
    if (this.buffer.length === 0) return;
    const token = this.credentialsService.token;
    if (!token) return;
    const batch = this.buffer;
    this.buffer = [];
    try {
      fetch(this.url, {
        method: 'POST',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + token,
        },
        body: JSON.stringify(batch),
      }).catch(() => undefined);
    } catch {
      this.buffer = batch.concat(this.buffer);
    }
  }

  /** Extrahiert das Segment nach /<key>(s)/ aus einer URL (Hash oder Slug). */
  private extractSeg(url: string, key: string): string {
    const match = new RegExp('/' + key + 's?/([^/?#]+)', 'i').exec(url || '');
    return match ? match[1] : '';
  }

  private isHome(url: string): boolean {
    const path = url.split('?')[0];
    return path === '/' || path.startsWith('/home');
  }

  private pageLabel(url: string): string {
    const path = url.split('?')[0];
    if (this.isHome(url)) return 'Startseite';
    if (/\/lessons?\//i.test(path)) return 'Lektion';
    if (/testexam|pruefung|exam/i.test(path)) return 'Probeprüfung';
    if (/overview|uebersicht/i.test(path)) return 'Lektionsübersicht';
    return path;
  }
}
